import type { TDocumentContent } from "@shared/entities/documentTemplate";
import type { TDocumentVariableKey } from "@shared/entities/documentVariables";

type AnyNode = {
  type: string;
  attrs?: Record<string, unknown>;
  content?: AnyNode[];
  [key: string]: unknown;
};

function substituteNode(
  node: AnyNode,
  values: Partial<Record<TDocumentVariableKey, string>>,
): AnyNode | AnyNode[] {
  if (node.type === "variable") {
    const key = node.attrs?.key as TDocumentVariableKey | undefined;
    const text = key !== undefined ? (values[key] ?? "") : "";
    // ProseMirror forbids empty text nodes — omit when the value is absent.
    if (!text) return [];
    return { type: "text", text };
  }

  if (node.content && node.content.length > 0) {
    const newContent: AnyNode[] = [];
    for (const child of node.content) {
      const result = substituteNode(child, values);
      if (Array.isArray(result)) {
        newContent.push(...result);
      } else {
        newContent.push(result);
      }
    }
    return { ...node, content: newContent };
  }

  return { ...node };
}

/**
 * Replace all VariableNodes in content with their resolved text values.
 * Keys not present in `values` are replaced with an empty string.
 * Unknown node types are left untouched.
 * Returns a new object — does not mutate the input.
 */
export function substituteVariables(
  content: TDocumentContent,
  values: Partial<Record<TDocumentVariableKey, string>>,
): TDocumentContent {
  const root = content as AnyNode;
  const result = substituteNode(root, values) as AnyNode;
  return result as TDocumentContent;
}
